import { range } from "lodash-es";

import { isCoordinate, minus, plus, rectangle } from "@/math/coordinates";
import type { BoardData, BoardIteration, IterationState } from "@/model/board";
import type { Rectangle, RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import type { Shape } from "@/model/shape";
import { fixLine } from "@/model/shape";
import { useIterationStore } from "@/store/iteration";
import { useSessionStore } from "@/store/session";
import { useGlobalI18n } from "@/translations/i18n";

import { BoardLocation, borderLines } from "./BoardLocation";

export const OBJECTIVES_INDEX = -1;
export const RISKS_INDEX = -2;
export const EMPTY_INDEX = -3;

const { t } = useGlobalI18n();

export class TeamBoardLocation extends BoardLocation {
  constructor(
    private iterations: IterationState[],
    private regionIndex: number,
  ) {
    super();
  }

  static of(
    iterations: IterationState[],
    value: RelativeCoordinate | number | string,
  ): TeamBoardLocation {
    return isCoordinate(value)
      ? TeamBoardLocation.fromCoordinate(iterations, value)
      : new TeamBoardLocation(iterations, +value);
  }

  static fromCoordinate(
    iterations: IterationState[],
    coord: RelativeCoordinate,
  ): TeamBoardLocation {
    const left = Math.floor(coord.x * columns(iterations));
    if (coord.y < 0.5) {
      return new TeamBoardLocation(iterations, left);
    }
    switch (left) {
      case 0:
        return new TeamBoardLocation(iterations, OBJECTIVES_INDEX);
      case 1:
        return new TeamBoardLocation(iterations, RISKS_INDEX);
      default: {
        const iter = left + columns(iterations) - 2;
        return iter < iterations.length
          ? new TeamBoardLocation(iterations, iter)
          : new TeamBoardLocation(iterations, EMPTY_INDEX);
      }
    }
  }

  override matches(coord: RelativeCoordinate) {
    return (
      TeamBoardLocation.fromCoordinate(this.iterations, coord).regionIndex === this.regionIndex
    );
  }

  get iterationId(): number | null {
    return this.regionIndex < 0 ? null : this.regionIndex;
  }

  get isIteration() {
    return this.iterationId !== null;
  }

  override id() {
    return "" + this.regionIndex;
  }

  override get name() {
    switch (this.regionIndex) {
      case OBJECTIVES_INDEX:
        return t("general.objectives");
      case RISKS_INDEX:
        return t("teamBoardLocation.risks");
      case EMPTY_INDEX:
        return " ";
      default:
        return t("general.namedIteration", {
          name: useSessionStore().iterations[this.regionIndex].name,
        });
    }
  }

  boardIteration(board: BoardData<"team">): BoardIteration | undefined {
    if (this.iterationId === null) return undefined; // For non-iteration locations
    return useIterationStore().iteration(board, this.iterationId);
  }

  get iterationState(): IterationState | undefined {
    return this.iterations[this.regionIndex];
  }

  override get bounds(): Rectangle<RelativeCoordinate> {
    return rectangle({ from: this.topLeft, size: relativeCoord(1 / this.columns, 0.5) });
  }

  override get freeBounds(): Rectangle<RelativeCoordinate> {
    const offset = relativeCoord(0, 0.08);
    return rectangle({
      from: plus(this.topLeft, offset),
      size: minus(relativeCoord(1 / this.columns, 0.5), offset),
    });
  }

  override get shapes(): Shape[] {
    return [
      ...borderLines,
      fixLine("h", relativeCoord(0, 0.5), relativeCoord(1, 0.5)),
      ...range(1, this.columns).map((col) =>
        fixLine(
          "v" + col,
          relativeCoord(col / this.columns, 0),
          relativeCoord(col / this.columns, 1),
        ),
      ),
    ];
  }

  private get topLeft(): RelativeCoordinate {
    switch (this.regionIndex) {
      case OBJECTIVES_INDEX:
        return relativeCoord(0, 0.5);
      case RISKS_INDEX:
        return relativeCoord(1 / this.columns, 0.5);
      case EMPTY_INDEX:
        return relativeCoord(1 - 1 / this.columns, 0.5);
      default:
        return this.regionIndex < this.columns
          ? relativeCoord(this.regionIndex / this.columns, 0)
          : relativeCoord((this.regionIndex - this.columns + 2) / this.columns, 0.5);
    }
  }

  get columns() {
    return columns(this.iterations);
  }

  isObjectives() {
    return this.regionIndex === OBJECTIVES_INDEX;
  }

  isRisks() {
    return this.regionIndex === RISKS_INDEX;
  }

  isEmpty() {
    return this.regionIndex === EMPTY_INDEX;
  }
}

function columns(iterations: unknown[]) {
  return Math.ceil(1 + iterations.length / 2);
}
